import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "@/hooks/auth";
import { useSecureAuth } from "@/hooks/auth/useSecureAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, Shield, HelpCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ForgotPasswordDialog from "@/components/auth/ForgotPasswordDialog";
import AccountStatusMessage from "@/components/auth/AccountStatusMessage";

const Login = () => {
  const navigate = useNavigate();
  const { session, user, profile, isLoading, requiresPasswordChange } = useSupabaseAuth();
  const { secureSignIn, isLoading: authLoading } = useSecureAuth();
  const { toast } = useToast();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Redirect authenticated users to the correct page based on password change requirement
  useEffect(() => {
    if (!isLoading && session && user) {
      // Check if password change is required BEFORE redirecting
      if (requiresPasswordChange) {
        console.log('User requires password change, redirecting to change-password');
        navigate("/change-password", { replace: true });
      } else {
        console.log('User is authenticated, redirecting to dashboard');
        navigate("/dashboard", { replace: true });
      }
    }
  }, [session, user, isLoading, requiresPasswordChange, navigate]);


  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    const { error } = await secureSignIn(email, password);

    if (error) {
      // Provide more helpful error messages
      let errorMessage = error.message;
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = "The email or password you entered is incorrect. Please check your credentials and try again.";
      } else if (error.message.includes('Too many requests')) {
        errorMessage = "Too many login attempts. Please wait a moment before trying again.";
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = "Please check your email and click the verification link before signing in.";
      }
      
      setError(errorMessage);
      toast({
        title: "Sign in failed",
        description: errorMessage,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in"
      });
      // Don't navigate here - let the auth state change handle it
    }
  };


  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <h2 className="text-xl font-semibold">Loading...</h2>
          <p className="text-sm text-muted-foreground mt-2">Checking your authentication status</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold flex items-center justify-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Neo Prism Academy
          </CardTitle>
          <CardDescription>
            Secure access to your learning platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Account Status Message */}
          {user && profile && (
            <div className="mb-4">
              <AccountStatusMessage />
            </div>
          )}

          {error && (
            <Alert className="mb-4" variant="destructive">
              <AlertDescription>
                <div className="space-y-2">
                  <p>{error}</p>
                  {error.includes('incorrect') && (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs underline"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot your password?
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="signin-password">Password</Label>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-xs"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot password?
                </Button>
              </div>
              <div className="relative">
                <Input
                  id="signin-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="rounded border-input"
              />
              <Label htmlFor="remember-me" className="text-sm">
                Remember me for 30 days
              </Label>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={authLoading || !email || !password}
            >
              {authLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Help Section */}
          <div className="mt-6 pt-4 border-t">
            <div className="bg-muted/30 p-3 rounded-lg">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                Need help accessing your account?
              </h4>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>• If you're a new student, check your email for an invitation</p>
                <p>• Your account may need approval by an administrator</p>
                <p>• Contact your instructor or IT support for assistance</p>
              </div>
            </div>
          </div>
        </CardContent>

        <ForgotPasswordDialog
          isOpen={showForgotPassword}
          onOpenChange={setShowForgotPassword}
        />
      </Card>
    </div>
  );
};

export default Login;
